/**
 * Os valores de uma linha de fatura — e o que cada coluna está MEDINDO.
 *
 * Três números convivem numa linha de cobrança e são confundidos com facilidade:
 *
 *   o nominal do TÍTULO      a dívida inteira daquele charge
 *   o valor da FATURA        o pedaço que aquele documento cobra
 *   o que já tem dinheiro    sinalizado (promessa) ou efetivado (carimbo)
 *
 * Eles coincidem no caso comum — uma fatura 1:1 com o título — e é isso que torna a confusão
 * silenciosa: o erro só aparece em título quebrado em frações, que é justamente onde alguém está
 * conferindo com atenção.
 *
 * Mora em `services/` e não junto de uma das telas de propósito: a página de Faturas e o modal de
 * faturas do pagamento mostram as MESMAS colunas, e importar de um modal para dentro de uma página
 * já quebrou a tela uma vez (ver `molecules/CommentCell`).
 */
import { formatBRL } from '../../../../utils/currencyUtils';

/**
 * O nominal do TÍTULO daquela fatura.
 *
 * "—" sem título eleito (fatura avulsa): cair no valor da fatura mudaria o SIGNIFICADO da coluna
 * linha a linha, que é o que torna uma coluna não confiável.
 */
export const nominalAmountOf = (row: any): string =>
    (row?.titleAmount == null ? '—' : formatBRL(row.titleAmount));

export const openAmountValue = (row: any): number | null =>
    (row?.titleRemainingAmount == null ? null : Number(row.titleRemainingAmount));

/** O que ainda falta no TÍTULO. Mesma regra do "—" acima. */
export const openAmountOf = (row: any): string => {
    const open = openAmountValue(row);
    return open == null ? '—' : formatBRL(open);
};

/**
 * O valor da FATURA — e, quando ela está paga, o que foi efetivamente RECEBIDO.
 *
 * A distinção importa porque os dois divergem exatamente nos casos que alguém vai investigar:
 * desconto, acréscimo, pagamento a maior. Numa fatura paga, o que aconteceu é o valor recebido; o
 * que ela pedia virou história, e mostrá-lo faria a linha contradizer o extrato.
 *
 * Só em PAID, e não "sempre que houver amount_paid": em fatura ABERTA o `amountPaid` é a marcação —
 * uma promessa, não um recebimento —, e trocá-lo pelo valor cobrado diria que a dívida encolheu
 * quando nada foi pago ainda.
 */
export const invoiceAmountOf = (row: any): string => formatBRL(invoiceAmountValue(row));

/**
 * O MESMO número de `invoiceAmountOf`, sem formatar — a regra (paga mostra o RECEBIDO) mora só
 * aqui, e a versão formatada acima deriva desta.
 *
 * ⚠️ A exportação para Excel NÃO usa isto, de propósito: lá "Valor Fatura" é sempre o cobrado e
 * "Valor Pago" vem numa coluna própria (ver `invoiceExportColumns`). A regra existe porque a TELA
 * tem uma coluna só de valor; a planilha tem duas.
 */
export const invoiceAmountValue = (row: any): number | null => {
    if (row?.status === 'PAID' && row?.amountPaid != null) return Number(row.amountPaid);
    return row?.amount == null ? null : Number(row.amount);
};
